import { useMemo, useState, Fragment, useCallback } from 'react';
import { Form, Table, Button, Spinner, OverlayTrigger, Tooltip, Toast, ToastContainer } from 'react-bootstrap';
import { TablePageLayout } from '../components/TablePageLayout';
import { formatDate } from '../utils/formatters';
import { useQuery } from '@tanstack/react-query';
import { fetchIntransitData } from '../api/intransitApi';
import { fetchAuthStatus } from '../api/authApi';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table';

export function IntransitBasePage({ mode, label, columns, expandedContent, globalFilterFn, searchHelp, getIsDone }) {
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [expanded, setExpanded] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const triggerNotification = useCallback((message) => {
    setToastMessage(message);
    setShowToast(true);
  }, []);

  const { data: authData } = useQuery({
    queryKey: ['auth-status'],
    queryFn: fetchAuthStatus,
  });

  const { data: intransitData = [], isLoading, isError, error, isFetching, dataUpdatedAt, isFetched } = useQuery({
    queryKey: ['intransit', mode],
    queryFn: fetchIntransitData,
    enabled: !!mode,
    refetchInterval: 60000, // Refetch every minute
  });

  const checkIsDone = useCallback((item) => {
    if (getIsDone) return getIsDone(item);
    return item.is_done || item.state === 'done';
  }, [getIsDone]);

  const sortedData = useMemo(() => {
    if (!intransitData) return [];
    return [...intransitData].sort((a, b) => {
      const aDone = checkIsDone(a);
      const bDone = checkIsDone(b);
      if (aDone && !bDone) return 1;
      if (!aDone && bDone) return -1;
      return 0;
    });
  }, [intransitData, checkIsDone]);

  const { containerRef, handleScroll } = useScrollRestoration(
    `intransitListScroll_${mode}`,
    isFetched
  );

  const table = useReactTable({
    data: sortedData,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      expanded,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => !!expandedContent,
    globalFilterFn: globalFilterFn,
    meta: {
      triggerNotification,
    },
  });

  const filterableColumns = useMemo(() => {
    const columnsWithFilters = ['user_id', 'purchaser'];
    return columnsWithFilters.filter(id => table.getColumn(id));
  }, [table]);

  const columnData = useMemo(() => {
    const result = {};
    filterableColumns.forEach(colId => {
      const counts = intransitData.reduce((acc, item) => {
        const val = item[colId];
        if (val) {
          acc[val] = (acc[val] || 0) + 1;
        }
        return acc;
      }, {});
      result[colId] = Object.entries(counts)
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => a.value.localeCompare(b.value));
    });
    return result;
  }, [intransitData, filterableColumns]);

  const filteredCount = table.getFilteredRowModel().rows.length;
  const totalCount = table.getCoreRowModel().rows.length;
  const isFiltered = filteredCount !== totalCount;

  if (authData && !authData.user?.permissions?.includes('intransit.limited_intransit')) {
    return (
      <TablePageLayout title={`Intransit: ${label}`}>
        <div className="p-3">
          <div className="alert alert-danger">
            You do not have permission to access this page.
          </div>
        </div>
      </TablePageLayout>
    );
  }

  return (
    <TablePageLayout
      title={`Intransit: ${label}`}
      action={(
        <div className="d-flex align-items-center gap-3">
          <div className="text-muted small d-flex flex-column align-items-end">
            {isFiltered && (
              <div className="fw-bold text-dark text-nowrap">
                {filteredCount} / {totalCount} shown
              </div>
            )}
            <div className="d-flex align-items-center">
              {(isFetching && !isLoading) ? (
                <div className="text-primary d-flex align-items-center">
                  <Spinner animation="border" size="sm" className="me-1" />
                  <span>Updating...</span>
                </div>
              ) : dataUpdatedAt ? (
                <span className="text-nowrap">
                  Last updated: {formatDate(new Date(dataUpdatedAt), 'HH:mm:ss')}
                </span>
              ) : null}
            </div>
          </div>
          {Object.keys(expanded).length > 0 && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setExpanded({})}
              className="text-nowrap"
            >
              Close all
            </Button>
          )}
          <div className="d-flex align-items-center position-relative">
            <Form.Control
              value={globalFilter ?? ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Search..."
              disabled={!intransitData.length}
              style={{ maxWidth: '18rem', paddingRight: searchHelp ? '2.5rem' : '0.75rem' }}
            />
            {searchHelp && (
              <OverlayTrigger
                placement="left"
                overlay={<Tooltip id="search-help-tooltip">{searchHelp}</Tooltip>}
              >
                <span 
                  className="position-absolute end-0 me-2 text-muted" 
                  style={{ cursor: 'help', lineHeight: 1 }}
                >
                  ⓘ
                </span>
              </OverlayTrigger>
            )}
          </div>
        </div>
      )}
      isLoading={isLoading}
      isError={isError}
      error={error}
      containerRef={containerRef}
      onScroll={handleScroll}
    >
      <Table hover className="mb-0 align-middle sticky-header">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}
                  style={{ cursor: header.column.getCanSort() ? 'pointer' : 'default', userSelect: 'none' }}
                >
                  {header.isPlaceholder
                    ? null
                    : (
                      <div className="d-flex flex-column align-items-start">
                        <div className="d-flex align-items-center mb-1">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: ' 🔼',
                            desc: ' 🔽',
                          }[header.column.getIsSorted()] ?? null}
                        </div>
                        {header.column.getCanFilter() && filterableColumns.includes(header.column.id) && (
                          <Form.Select
                            size="sm"
                            value={(header.column.getFilterValue()) ?? ''}
                            onChange={(e) => header.column.setFilterValue(e.target.value || undefined)}
                            onClick={(e) => e.stopPropagation()}
                            style={{ maxWidth: '120px' }}
                          >
                            <option value="">All ({intransitData.length})</option>
                            {(columnData[header.column.id] || []).map(({ value, count }) => (
                              <option key={value} value={value}>
                                {value} ({count})
                              </option>
                            ))}
                          </Form.Select>
                        )}
                      </div>
                    )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => {
            const isDone = checkIsDone(row.original);
            return (
              <Fragment key={row.id}>
                <tr
                  onClick={(e) => {
                    const selection = window.getSelection();
                    if (selection && selection.toString().length > 0) return;
                    if (expandedContent) row.getToggleExpandedHandler()(e);
                  }}
                  style={{ cursor: expandedContent ? 'pointer' : 'default' }}
                  className={isDone ? 'table-success' : ''}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
                {row.getIsExpanded() && expandedContent && (
                  <tr>
                    <td colSpan={row.getVisibleCells().length} className="bg-light p-4">
                      {expandedContent(row.original)}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </Table>
      <ToastContainer position="bottom-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast onClose={() => setShowToast(false)} show={showToast} delay={5000} autohide bg="success">
          <Toast.Body className="text-white">
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </TablePageLayout>
  );
}
